import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Col, Row } from 'reactstrap';
import { useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import uuid from 'react-uuid';

import { ReactComponent as AddRoundIcon } from 'assets/icon-add-round.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/icon-arrow-down.svg';
import { ReactComponent as ArrowUpIcon } from 'assets/icon-arrow-up.svg';
import { ReactComponent as TrashIcon } from 'assets/icon-trash.svg';
import { LabelInput, LabelRequiredInput, LabelError } from 'shared/components/Labels';
import { TextField } from 'shared/components/form/Inputs';
import { Button, ButtonIcon } from 'shared/components/Buttons';
import DropdownSearch from 'shared/components/DropdownSearch';

import FieldType from 'shared/helpers/fieldType';
import { CustomFieldProps } from 'shared/prop-types/ReducerProps';
import { colors } from 'shared/defaultTheme';
import { cleanSpaceString } from 'shared/utils';

const ListForm = ({ defaultValue, multiple, onCancel, onSubmit }) => {
  const { t } = useTranslation();
  const customField = useSelector((state) => state.customField);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const [members, setMembers] = useState([]);
  const [errorMembers, setErrorMembers] = useState('');
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: defaultValue
      ? {
          fieldTitle: defaultValue.name,
          groupId: defaultValue.parentId,
        }
      : undefined,
  });

  const getNameSelectedLanguage = (i18List) => {
    if (i18List) {
      const findName = i18List.find((x) => x.i18Code === selectedDefaultLanguage);
      if (findName && findName?.i18Name !== '') {
        return findName?.i18Name;
      }
      return i18List[0].i18Name;
    }
    return null;
  };

  useEffect(() => {
    if (defaultValue) {
      setValue('fieldTitle', getNameSelectedLanguage(defaultValue.i18List));
    }
  }, [defaultValue]);

  useEffect(() => {
    if (defaultValue) {
      setMembers(defaultValue.members);
      defaultValue.members.forEach((member) =>
        setValue(member.id, getNameSelectedLanguage(member.i18List))
      );
    }
    return () => {
      setMembers([]);
      setErrorMembers('');
    };
  }, []);

  const addNewOption = () => {
    const newMember = {
      id: uuid(),
      i18List: [
        {
          i18Code: '',
          i18Name: '',
        },
      ],
      order: members.length
        ? [...members].sort((memberA, memberB) => memberB.order - memberA.order)[0].order + 1
        : 1,
    };
    setMembers([...members, newMember]);
    setErrorMembers('');
  };

  const onUpOption = (item, fromIndex, toIndex) => {
    if (toIndex < 0) {
      return;
    }
    const membersSliced = members.slice();
    membersSliced.splice(toIndex, 0, membersSliced.splice(fromIndex, 1)[0]);
    const newMembers = membersSliced.map((member, index) =>
      member.id === item.id
        ? { ...member, order: index, className: 'animate-up' }
        : { ...member, order: index }
    );
    setMembers(newMembers);
    // Clear animation
    setTimeout(() => {
      setMembers(newMembers.map((member) => ({ ...member, className: undefined })));
    }, 1000);
  };

  const onDownOption = (item, fromIndex, toIndex) => {
    if (toIndex > members.length) {
      return;
    }
    const membersSliced = members.slice();
    membersSliced.splice(toIndex, 0, membersSliced.splice(fromIndex, 1)[0]);
    const newMembers = membersSliced.map((member, index) =>
      member.id === item.id
        ? { ...member, order: index, className: 'animate-down' }
        : { ...member, order: index }
    );
    setMembers(newMembers);
    // Clear animation
    setTimeout(() => {
      setMembers(newMembers.map((member) => ({ ...member, className: undefined })));
    }, 1000);
  };

  const onRemoveOption = (memberId) => {
    let order = 1;
    const currentMembers = [...members]
      .sort((memberA, memberB) => memberA.order - memberB.order)
      .filter((member) => member.id !== memberId)
      .map((member) => {
        const newMemberOrder = { ...member, order };
        order += 1;
        return newMemberOrder;
      });
    setMembers(currentMembers);
    if (currentMembers.length <= 0) {
      setErrorMembers(t('general_required_field'));
    }
  };

  const onSubmitForm = (values) => {
    if (members.length > 0) {
      onSubmit(
        multiple,
        values,
        members.map((member) => ({
          ...member,
          i18List: [
            ...member.i18List.filter(
              (x) => selectedDefaultLanguage !== x.i18Code && x.i18Code !== ''
            ),
            {
              i18Name: values[member.id],
              i18Code: selectedDefaultLanguage,
            },
          ],
        }))
      );
    }
    setErrorMembers(t('general_required_field'));
  };

  return (
    <Row className="row-content w-100">
      <Col sm={12}>
        <form className="form form-field" noValidate onSubmit={handleSubmit(onSubmitForm)}>
          <Row className="w-100">
            <Col sm={12}>
              <div className="form__form-group">
                <LabelRequiredInput
                  className="form__form-group-label"
                  label={t('custom_field_form_title_label')}
                  color={colors.black}
                  error={errors.fieldTitle !== undefined}
                />
                <Controller
                  name="fieldTitle"
                  control={control}
                  defaultValue=""
                  rules={{ required: { value: true, message: t('general_required_field') } }}
                  render={({ field }) => (
                    <TextField
                      value={field.value}
                      onChange={(e) => {
                        const newEvent = cleanSpaceString(e);
                        field.onChange(newEvent);
                      }}
                      onBlur={field.onBlur}
                      error={errors.fieldTitle !== undefined}
                    />
                  )}
                />
                {errors.fieldTitle && <LabelError label={errors.fieldTitle.message} />}
              </div>
            </Col>
          </Row>
          {[...members]
            .sort((memberA, memberB) => memberA.order - memberB.order)
            .map((member, index) => (
              <React.Fragment key={member.id}>
                <Row className={classNames(['w-100', member.className])}>
                  <Col className="remove-padding-left" sm={10}>
                    <div className="form__form-group">
                      <LabelRequiredInput
                        className="form__form-group-label"
                        label={t('custom_field_list_option')}
                        color={colors.black}
                        error={errors[member.id] !== undefined}
                      />
                      <Controller
                        name={member.id}
                        control={control}
                        defaultValue=""
                        rules={{
                          required: { value: true, message: t('general_required_field') },
                        }}
                        render={({ field }) => (
                          <TextField
                            value={field.value}
                            onChange={(e) => {
                              const newEvent = cleanSpaceString(e);
                              field.onChange(newEvent);
                            }}
                            onBlur={field.onBlur}
                            error={errors[member.id] !== undefined}
                          />
                        )}
                      />
                      {errors[member.id] && <LabelError label={errors[member.id].message} />}
                    </div>
                  </Col>
                  <Col
                    className="d-flex flex-row justify-content-end align-items-center remove-padding-right"
                    sm={2}
                  >
                    <ButtonIcon
                      className="action-icon"
                      Icon={() => <ArrowDownIcon />}
                      onClick={() => onDownOption(member, index, index + 1)}
                    />
                    <ButtonIcon
                      className="action-icon"
                      Icon={() => <ArrowUpIcon />}
                      onClick={() => onUpOption(member, index, index - 1)}
                    />
                    <ButtonIcon
                      className="action-icon"
                      Icon={() => <TrashIcon />}
                      onClick={() => onRemoveOption(member.id)}
                    />
                  </Col>
                </Row>
              </React.Fragment>
            ))}
          {errorMembers && <LabelError label={errorMembers} />}
          <Row className="w-100 row-border-bottom">
            <Col sm={12}>
              <button type="button" className="button-icon" onClick={addNewOption}>
                {multiple ? t('custom_field_add_checkbox') : t('custom_field_add_option')}
                <AddRoundIcon />
              </button>
            </Col>
          </Row>
          <Row className="w-100">
            <Col sm={12}>
              <div className="form__form-group">
                <LabelInput
                  className="form__form-group-label"
                  label={t('custom_field_choose_group')}
                  color={colors.black}
                  error={errors.groupId !== undefined}
                />
                <Controller
                  name="groupId"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <DropdownSearch
                      id="list-form-dropdown-search"
                      options={customField.customFields
                        .filter((item) => item.type === FieldType.GROUP)
                        .map((item) => ({
                          name: getNameSelectedLanguage(item.i18List),
                          value: item.id,
                        }))}
                      value={field.value}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      error={errors.groupId !== undefined}
                    />
                  )}
                />
                {errors.groupId && <LabelError label={errors.groupId.message} />}
              </div>
            </Col>
          </Row>
          <Row className="w-100">
            <Col className="d-flex flex-row justify-content-end" sm={12}>
              <Button
                className="text-uppercase mr-4"
                type="button"
                color="secondary"
                label={t('general_button_cancel')}
                onClick={onCancel}
              />
              <Button
                className="text-uppercase"
                type="submit"
                color="primary"
                label={t(defaultValue ? 'general_button_update' : 'general_add')}
              />
            </Col>
          </Row>
        </form>
      </Col>
    </Row>
  );
};

ListForm.defaultProps = {
  defaultValue: undefined,
  multiple: false,
};

ListForm.propTypes = {
  defaultValue: CustomFieldProps,
  multiple: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default ListForm;
